
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PropertyBasicDetailsSection } from "./details/PropertyBasicDetailsSection";
import { PropertyStructuralDetailsSection } from "./details/PropertyStructuralDetailsSection";
import { PropertyMaintenanceDetailsSection } from "./details/PropertyMaintenanceDetailsSection";
import { PropertyStatusSection } from "./details/PropertyStatusSection";
import { ImportPropertyDataButton } from "./ImportPropertyDataButton";
import { RentCastPropertyData } from "@/types/rentcast";

interface PropertyDetailsProps {
  bedrooms: string;
  setBedrooms: (value: string) => void;
  bathrooms: string;
  setBathrooms: (value: string) => void;
  squareFeet: string;
  setSquareFeet: (value: string) => void;
  propertyType: string;
  setPropertyType: (value: string) => void;
  units: string;
  setUnits: (value: string) => void;

  foundationType: string;
  setFoundationType: (value: string) => void;
  yearBuilt: string;
  setYearBuilt: (value: string) => void;
  lotSize: string;
  setLotSize: (value: string) => void;
  roofAge: string;
  setRoofAge: (value: string) => void;
  windowType: string;
  setWindowType: (value: string) => void;
  furnaceAge: string;
  setFurnaceAge: (value: string) => void;
  waterHeaterAge: string;
  setWaterHeaterAge: (value: string) => void;
  architecture: string;
  setArchitecture: (value: string) => void;
  parking: string;
  setParking: (value: string) => void;
  status?: string;
  setStatus?: (value: string) => void;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  onImportData?: (data: RentCastPropertyData) => void;
  errors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
}

export const PropertyDetailsSection = ({
  bedrooms,
  setBedrooms,
  bathrooms,
  setBathrooms,
  squareFeet,
  setSquareFeet,
  propertyType,
  setPropertyType,
  units,
  setUnits,

  foundationType,
  setFoundationType,
  yearBuilt,
  setYearBuilt,
  lotSize,
  setLotSize,
  roofAge,
  setRoofAge,
  windowType,
  setWindowType,
  furnaceAge,
  setFurnaceAge,
  waterHeaterAge,
  setWaterHeaterAge,
  architecture,
  setArchitecture,
  parking,
  setParking,
  status,
  setStatus,
  address,
  city,
  state,
  zipCode,
  onImportData,
  errors = {},
  clearFieldError,
}: PropertyDetailsProps) => {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">Property Details</h2>
          <Separator />
          
          <div className="grid grid-cols-1 gap-8">
            {/* Status Section (shown conditionally) */}
            {status !== undefined && setStatus !== undefined && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                <PropertyStatusSection 
                  status={status} 
                  setStatus={setStatus} 
                />
              </div>
            )}
            
            {/* Basic Details Section */}
            <div>
              <h3 className="text-lg font-medium mb-4">Basic Details</h3>
              
              {/* Import Button - Only show if address data is available and callback is provided */}
              {address && city && state && zipCode && onImportData && (
                <div className="mb-6">
                  <ImportPropertyDataButton
                    address={address}
                    city={city}
                    state={state}
                    zipCode={zipCode}
                    onDataImported={onImportData}
                  />
                </div>
              )}
              
              <PropertyBasicDetailsSection
                bedrooms={bedrooms}
                setBedrooms={setBedrooms}
                bathrooms={bathrooms}
                setBathrooms={setBathrooms}
                squareFeet={squareFeet}
                setSquareFeet={setSquareFeet}
                propertyType={propertyType}
                setPropertyType={setPropertyType}
                units={units}
                setUnits={setUnits}
                errors={errors}
                clearFieldError={clearFieldError}
              />

            </div>
            
            {/* Structural Details Section */}
            <div>
              <h3 className="text-lg font-medium mb-4">Structural Details</h3>
              <PropertyStructuralDetailsSection
                foundationType={foundationType}
                setFoundationType={setFoundationType}
                yearBuilt={yearBuilt}
                setYearBuilt={setYearBuilt}
                lotSize={lotSize}
                setLotSize={setLotSize}
                architecture={architecture}
                setArchitecture={setArchitecture}
                parking={parking}
                setParking={setParking}
                errors={errors}
                clearFieldError={clearFieldError}
              />
            </div>
            
            {/* Maintenance Details Section */}
            <div>
              <h3 className="text-lg font-medium mb-4">Maintenance Details</h3>
              <PropertyMaintenanceDetailsSection
        roofAge={roofAge}
        setRoofAge={setRoofAge}
        windowType={windowType}
        setWindowType={setWindowType}
        furnaceAge={furnaceAge}
                setFurnaceAge={setFurnaceAge}
                waterHeaterAge={waterHeaterAge}
                setWaterHeaterAge={setWaterHeaterAge}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
